import { computed, inject } from '@angular/core'

import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals'

import { Observable, lastValueFrom } from 'rxjs'

import {
  CreateResourceDataSlicesPayload,
  ResourceDataSlice,
  SliceType,
  UpdateResourceDataSlicesPayload,
} from '@flowx/designer/modules/core/dictionary/data-mapping.dictionary'
import { AppResource, mapResource } from '@flowx/designer/shared/entities/application.entity'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'

export interface SlicesState {
  slices: {
    inputSlice: Partial<ResourceDataSlice>
    outputSlice: Partial<ResourceDataSlice>
    loading: boolean
  }
}

export interface SliceApiService {
  getSlices(resourceId: string): Observable<AppResource<ResourceDataSlice[]>>
  createSlice(
    resourceId: string,
    slice: CreateResourceDataSlicesPayload
  ): Observable<AppResource<ResourceDataSlice>>
  updateSlice(
    resourceId: string,
    slice: UpdateResourceDataSlicesPayload
  ): Observable<AppResource<ResourceDataSlice>>
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function withSlices(config: {
  getIdentifier: (state: any) => string
  createApiService: () => SliceApiService
}) {
  return signalStoreFeature(
    withState<SlicesState>({
      slices: {
        inputSlice: { sliceItems: [] },
        outputSlice: { sliceItems: [] },
        loading: false,
      },
    }),

    withMethods((store) => {
      const toastService = inject(ToastMessageService)
      const apiService = config.createApiService()
      return {
        getSlices: async (resourceId?: string) => {
          const id = resourceId || config.getIdentifier(store as any)
          patchState(store, { slices: { ...store.slices(), loading: true } })

          try {
            const slices = mapResource(await lastValueFrom(apiService.getSlices(id)))

            patchState(store, {
              slices: {
                ...store.slices(),
                inputSlice: slices.find((slice) => slice.type === SliceType.INPUT) || {
                  sliceItems: [],
                },
                outputSlice: slices.find((slice) => slice.type === SliceType.OUTPUT) || {
                  sliceItems: [],
                },
                loading: false,
              },
            })
            return slices
          } catch (err) {
            toastService.showErrorMessage(err?.error?.title ?? 'Error fetching slices!')
            patchState(store, { slices: { ...store.slices(), loading: false } })
          }
        },

        addSlice: async (slice: CreateResourceDataSlicesPayload, isInput: boolean) => {
          try {
            const identifier = config.getIdentifier(store as any)
            const createdSlice = mapResource(
              await lastValueFrom(apiService.createSlice(identifier, slice))
            )

            patchState(store, {
              slices: {
                ...store.slices(),
                [isInput ? 'inputSlice' : 'outputSlice']: createdSlice,
              },
            })
            return createdSlice
          } catch (err) {
            toastService.showErrorMessage(
              err?.error?.title ?? `Error creating ${isInput ? 'input' : 'output'} slice!`
            )
          }
        },

        updateSlice: async (slice: UpdateResourceDataSlicesPayload, isInput: boolean) => {
          try {
            const identifier = config.getIdentifier(store as any)
            const updatedSlice = mapResource(
              await lastValueFrom(apiService.updateSlice(identifier, slice))
            )

            patchState(store, {
              slices: {
                ...store.slices(),
                [isInput ? 'inputSlice' : 'outputSlice']: updatedSlice,
              },
            })
            return updatedSlice
          } catch (err) {
            toastService.showErrorMessage(
              err?.error?.title ?? `Error updating ${isInput ? 'input' : 'output'} slice!`
            )
          }
        },
      }
    }),

    withComputed((store) => ({
      inputSlice: computed(() => store.slices().inputSlice),
      outputSlice: computed(() => store.slices().outputSlice),
      inputSlices: computed(() => store.slices().inputSlice?.sliceItems ?? []),
      outputSlices: computed(() => store.slices().outputSlice?.sliceItems ?? []),
      slicesLoading: computed(() => store.slices().loading),
    }))
  )
}
